/**
 * WB4 Phase 3 — Palette intent timeline event builders.
 * Chain: ai_suggested → user_approved → action_executed (kernel).
 */

import type { WorkbenchTimelineEventInput } from './workbench-timeline-event';
import type { WorkbenchRiskTier } from './workbench-action-safety-policy';
import type { PaletteOperationalIntent } from './workbench-palette-intent';
import { PALETTE_OPERATIONAL_INTENT_SPECS } from './workbench-palette-intent';

export const WORKBENCH_TIMELINE_EVENT_AI_SUGGESTED = 'ai_suggested' as const;
export const WORKBENCH_TIMELINE_EVENT_USER_APPROVED = 'user_approved' as const;

export type PaletteIntentTimelineEventType =
  | typeof WORKBENCH_TIMELINE_EVENT_AI_SUGGESTED
  | typeof WORKBENCH_TIMELINE_EVENT_USER_APPROVED;

export interface BuildPaletteIntentTimelineEventInput {
  tenantId: string;
  domain: string;
  objectType: string;
  objectId: string;
  intent: PaletteOperationalIntent;
  actionId: string;
  actionLabel: string;
  riskTier: WorkbenchRiskTier;
  actor: { userId: string; displayName?: string; role?: string };
  interpretationSummary?: string;
  id?: string;
}

function buildBasePaletteIntentTimelineEvent(
  type: PaletteIntentTimelineEventType,
  input: BuildPaletteIntentTimelineEventInput
): WorkbenchTimelineEventInput {
  const spec = PALETTE_OPERATIONAL_INTENT_SPECS[input.intent];
  const title =
    type === WORKBENCH_TIMELINE_EVENT_AI_SUGGESTED
      ? `AI 제안: ${spec.label}`
      : `사용자 승인: ${spec.label}`;

  return {
    ...(input.id ? { id: input.id } : {}),
    tenantId: input.tenantId,
    domain: input.domain,
    objectType: input.objectType,
    objectId: input.objectId,
    type,
    title,
    ...(input.interpretationSummary ? { description: input.interpretationSummary } : {}),
    actor: input.actor,
    action: {
      actionId: input.actionId,
      actionLabel: input.actionLabel,
      riskTier: input.riskTier,
      surface: 'palette',
    },
    related: {
      domainEventType: `palette_intent:${input.intent}`,
    },
    recoverability: {
      mode: 'none',
      available: false,
      reason: 'palette_intent_governance',
    },
    severity: type === WORKBENCH_TIMELINE_EVENT_USER_APPROVED ? 'success' : 'info',
  };
}

export function buildPaletteIntentAiSuggestedTimelineEvent(
  input: BuildPaletteIntentTimelineEventInput
): WorkbenchTimelineEventInput {
  return buildBasePaletteIntentTimelineEvent(WORKBENCH_TIMELINE_EVENT_AI_SUGGESTED, input);
}

export function buildPaletteIntentUserApprovedTimelineEvent(
  input: BuildPaletteIntentTimelineEventInput
): WorkbenchTimelineEventInput {
  return buildBasePaletteIntentTimelineEvent(WORKBENCH_TIMELINE_EVENT_USER_APPROVED, input);
}
